/**
 * <s-product-gallery> — one-image-at-a-time product media slider.
 *
 * Scrolling and swiping work natively through a scroll-snap track; this
 * component only enhances the prev/next arrows and dot pagination and keeps
 * their state in sync with the visible slide.
 *
 * Required markup:
 *   [data-gallery-track]        — horizontal scroll-snap track.
 *   [data-gallery-slide]        — direct slide children of the track.
 * Optional markup (buttons use commandfor="<this element's id>"):
 *   button[command="--prev"]    — previous slide.
 *   button[command="--next"]    — next slide.
 *   button[command="--go"][data-index] — jump to slide N (0-based); also the dot.
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #slides = [];
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;
    this.#slides = Array.from(this.#track.querySelectorAll(':scope > [data-gallery-slide]'));

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  #handleCommand = (event) => {
    if (event.command === '--prev') this.#goTo(this.#index - 1);
    else if (event.command === '--next') this.#goTo(this.#index + 1);
    else if (event.command === '--go') {
      const index = Number(event.source?.dataset.index);
      if (Number.isInteger(index)) this.#goTo(index);
    }
  };

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#update());
  };

  #goTo(index) {
    const target = this.#slides[Math.max(0, Math.min(index, this.#slides.length - 1))];
    if (!target) return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rtl = getComputedStyle(this.#track).direction === 'rtl';
    const offset = rtl
      ? -(this.#track.offsetWidth - target.offsetLeft - target.offsetWidth)
      : target.offsetLeft;
    this.#track.scrollTo({ left: offset, behavior: reduceMotion ? 'auto' : 'smooth' });
  }

  #update() {
    const width = this.#track.clientWidth || 1;
    const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
    this.#index = Math.max(0, Math.min(index, this.#slides.length - 1));

    const last = this.#slides.length - 1;
    for (const button of this.querySelectorAll('button[command="--prev"]')) {
      button.disabled = this.#index <= 0;
    }
    for (const button of this.querySelectorAll('button[command="--next"]')) {
      button.disabled = this.#index >= last;
    }
    for (const dot of this.querySelectorAll('button[command="--go"]')) {
      const active = Number(dot.dataset.index) === this.#index;
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
